import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Plane, Shield, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const PROFILE_LINK = "https://trackmypackage.app/join/shipcostarica";
const PLANE_IMG = "https://customer-assets.emergentagent.com/job_fly-ship-cr/artifacts/3xo1ic5d_image.png";

const Hero = () => {
  const { t, lang } = useLang();
  const heroRef = useRef(null);

  // Typewriter effect for headline
  const fullTitle = `${t.hero.title1} ${t.hero.title2} ${t.hero.title3}`;
  const [typed, setTyped] = useState("");
  const [doneTyping, setDoneTyping] = useState(false);

  useEffect(() => {
    setTyped("");
    setDoneTyping(false);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      if (i > fullTitle.length) {
        clearInterval(interval);
        setDoneTyping(true);
      } else {
        setTyped(fullTitle.slice(0, i));
      }
    }, 38);
    return () => clearInterval(interval);
  }, [fullTitle, lang]);

  // Drive plane horizontally based on overall page scroll
  const { scrollYProgress } = useScroll();
  // Plane starts visible on the right (under heading) and flies left across the page
  const planeX = useTransform(scrollYProgress, [0, 1], ["30vw", "-65vw"]);
  const planeY = useTransform(scrollYProgress, [0, 0.5, 1], ["0px", "-30px", "20px"]);
  const planeRotate = useTransform(scrollYProgress, [0, 1], [8, -6]);
  const planeOpacity = useTransform(scrollYProgress, [0, 0.92, 1], [1, 1, 0]);

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden pt-28 pb-24 sm:pt-32 sm:pb-32 min-h-[100svh] flex items-center"
    >
      {/* Clean blue sky gradient */}
      <div className="absolute inset-0 sky-gradient" aria-hidden="true" />

      {/* Two soft drifting clouds — subtle, not repeating */}
      <div
        className="absolute top-[18%] left-[-10%] w-[420px] h-[140px] opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 70% at 30% 50%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 35%, rgba(255,255,255,0) 70%), radial-gradient(ellipse 45% 80% at 70% 45%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%)",
          filter: "blur(6px)",
          animation: "pan-clouds 90s linear infinite",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute top-[55%] right-[-15%] w-[520px] h-[170px] opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 70% at 40% 50%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.5) 40%, rgba(255,255,255,0) 75%), radial-gradient(ellipse 40% 60% at 75% 60%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)",
          filter: "blur(8px)",
          animation: "pan-clouds 130s linear infinite reverse",
          animationDelay: "-20s",
        }}
        aria-hidden="true"
      />

      {/* Soft top cream haze for navbar contrast */}
      <div
        className="absolute inset-x-0 top-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(245,239,232,0.7) 0%, rgba(245,239,232,0) 100%)",
        }}
        aria-hidden="true"
      />
      {/* Soft bottom cream fade into next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(245,239,232,0) 0%, rgba(245,239,232,0.85) 100%)",
        }}
        aria-hidden="true"
      />

      {/* Glow accents */}
      <div className="absolute top-10 right-12 w-72 h-72 rounded-full bg-brand-gold/40 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-brand-red/15 blur-3xl" aria-hidden="true" />

      {/* Centered content */}
      <div className="relative w-full max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-2 rounded-full bg-white/75 backdrop-blur-md border border-brand-navy/15 px-4 py-2 mb-7 shadow-sm"
        >
          <Plane size={14} className="text-brand-red" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-navy">
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-black text-[36px] leading-[1.02] sm:text-6xl lg:text-7xl tracking-[-0.03em] text-brand-navy max-w-4xl mx-auto min-h-[3.6em] sm:min-h-[3.2em]"
          data-testid="hero-headline"
        >
          {typed}
          <span
            className={`inline-block w-[3px] sm:w-[4px] h-[0.9em] bg-brand-red ml-1 align-middle ${
              doneTyping ? "opacity-0 transition-opacity duration-700" : "animate-pulse"
            }`}
            aria-hidden="true"
          />
        </motion.h1>

        {/* Reserved space below headline so the layout breathes where the plane initially sits */}
        <div className="h-[60px] sm:h-[80px] lg:h-[100px] w-full" aria-hidden="true" />

        {/* Front-and-center CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-cta-card"
          className="relative mt-2 mx-auto max-w-2xl"
        >
          <div
            className="absolute -inset-2 bg-gradient-to-r from-brand-red/30 via-brand-gold/30 to-brand-blue/30 blur-2xl rounded-[36px] -z-0"
            aria-hidden="true"
          />

          <div className="relative rounded-[28px] bg-white/95 backdrop-blur-xl border border-brand-navy/10 shadow-[0_30px_70px_-20px_rgba(14,42,86,0.4)] p-7 sm:p-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-red/10 text-brand-red px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
              <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                {t.hero.ctaBadge}
              </span>
            </div>

            <h2 className="font-heading font-black text-[26px] sm:text-4xl lg:text-5xl tracking-[-0.02em] text-brand-navy leading-[1.05]">
              {t.hero.ctaTitle}
            </h2>

            <a
              href={PROFILE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("click_create_profile", { location: "hero" })}
              data-testid="hero-cta-primary"
              className="mt-7 group inline-flex items-center justify-center gap-3 rounded-full bg-brand-red text-white font-heading font-black text-base sm:text-xl px-6 sm:px-8 py-4 sm:py-5 w-full sm:w-auto shadow-[0_18px_40px_-12px_rgba(216,35,42,0.65)] hover:bg-brand-navy hover:shadow-[0_18px_40px_-12px_rgba(14,42,86,0.7)] transition-all"
            >
              {t.hero.cta}
              <ArrowRight size={20} className="group-hover:translate-x-1.5 transition-transform" />
            </a>

            <div className="mt-4 text-[11px] sm:text-xs text-brand-navy/55 font-semibold tracking-wide">
              {t.hero.ctaFootnote}
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-10 max-w-2xl mx-auto text-base sm:text-lg text-brand-navy/75 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-10 grid grid-cols-3 gap-3 sm:gap-5 max-w-2xl mx-auto"
        >
          {[
            { l: t.hero.stat1, v: t.hero.stat1v, icon: Plane },
            { l: t.hero.stat2, v: t.hero.stat2v, icon: Clock },
            { l: t.hero.stat3, v: t.hero.stat3v, icon: Shield },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 + i * 0.1 }}
              data-testid={`hero-stat-${i}`}
              className="rounded-2xl bg-white/70 backdrop-blur-md border border-brand-navy/10 p-3 sm:p-5 shadow-sm text-left"
            >
              <s.icon size={16} className="text-brand-red mb-2" />
              <div className="font-heading font-black text-base sm:text-2xl text-brand-navy tracking-tight">
                {s.v}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-brand-navy/60 font-semibold mt-0.5">
                {s.l}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Fixed flying plane - visible across the whole page while scrolling.
          Starts on the right (visible under the heading on initial load), flies left as user scrolls. */}
      <motion.div
        style={{ x: planeX, y: planeY, opacity: planeOpacity }}
        className="fixed top-[40vh] sm:top-[38vh] left-1/2 -translate-x-1/2 z-[5] pointer-events-none will-change-transform"
        aria-hidden="true"
        data-testid="hero-plane-track"
      >
        <motion.div style={{ rotate: planeRotate }} className="relative">
          <div className="relative w-[210px] sm:w-[340px] lg:w-[460px]">
            <div className="absolute top-[58%] left-[80%] w-24 sm:w-36 h-1.5 rounded-full bg-white/80 blur-[3px]" />
            <div className="absolute top-[68%] left-[85%] w-16 sm:w-24 h-1 rounded-full bg-white/55 blur-[2px]" />

            <motion.img
              src={PLANE_IMG}
              alt=""
              draggable={false}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-auto drop-shadow-[0_22px_28px_rgba(14,42,86,0.3)] select-none scale-x-[-1]"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
